import type { ViewColumnsConfig } from "@redotech/effect-api-shared/rpc/view-folder";
import { arraysEqual } from "@redotech/util/array";
import type {
  ColumnOrderState,
  ColumnSizingState,
  VisibilityState,
} from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useLocalStorage } from "usehooks-ts";
import { getSupportTicketsTableColumns } from "../table/columns/support-tickets-columns";
import type { SupportTicketColumnDef } from "../table/types";

interface ActiveView {
  columns?: ViewColumnsConfig | null;
}

export interface SupportTicketsTableColumnState {
  columns: SupportTicketColumnDef[];
  hideableColumnIds: readonly string[];
  columnOrder: ColumnOrderState;
  setColumnOrder: (order: ColumnOrderState) => void;
  columnSizing: ColumnSizingState;
  setColumnSizing: (sizing: ColumnSizingState) => void;
  columnVisibility: VisibilityState;
  setColumnVisibility: (visibility: VisibilityState) => void;
  isColumnStateDirty: boolean;
  effectiveColumnsConfig: ViewColumnsConfig | null;
  resetColumnState: () => void;
}

function visibilityRecordsEqual(
  a: VisibilityState,
  b: VisibilityState,
): boolean {
  if (a === b) {
    return true;
  }
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) {
    return false;
  }
  for (const k of aKeys) {
    if (a[k] !== b[k]) {
      return false;
    }
  }
  return true;
}

/**
 * Owns column ordering, visibility, sizing, and per-view dirty/effective state
 * for the support tickets table.
 *
 * Order and visibility live in React only — drags and visibility toggles are
 * pending until the user explicitly clicks "Save as new view" or "Update
 * view". A page refresh discards pending state (matches the spec). Sizing is
 * persisted to localStorage because it is global, not per-view.
 *
 * When the user navigates between views, pending state is discarded so edits
 * made on view A do not bleed onto view B.
 */
export function useSupportTicketsTableColumnState(
  activeView: ActiveView | null,
  activeViewId: string | null,
): SupportTicketsTableColumnState {
  const [pendingColumnOrder, setPendingColumnOrder] =
    useState<ColumnOrderState | null>(null);
  const [pendingColumnVisibility, setPendingColumnVisibility] =
    useState<VisibilityState | null>(null);

  const [prevActiveViewId, setPrevActiveViewId] = useState(activeViewId);
  if (activeViewId !== prevActiveViewId) {
    setPrevActiveViewId(activeViewId);
    if (pendingColumnOrder !== null) {
      setPendingColumnOrder(null);
    }
    if (pendingColumnVisibility !== null) {
      setPendingColumnVisibility(null);
    }
  }

  const [columnSizing, setColumnSizing] = useLocalStorage<ColumnSizingState>(
    "redo.support-v2.support-tickets-table.column-sizing",
    {},
  );

  const columns = useMemo(
    () => getSupportTicketsTableColumns({ enablePinning: true }),
    [],
  );

  const hideableColumnIds = useMemo(
    () =>
      columns.flatMap((c) => (c.enableHiding !== false && c.id ? [c.id] : [])),
    [columns],
  );

  const storedOrder = activeView?.columns?.order ?? null;
  const storedVisibility = activeView?.columns?.visibility ?? null;

  const columnOrder = useMemo<ColumnOrderState>(() => {
    if (pendingColumnOrder !== null) {
      return pendingColumnOrder;
    }
    if (storedOrder) {
      return [...storedOrder];
    }
    return [];
  }, [pendingColumnOrder, storedOrder]);

  const columnVisibility = useMemo<VisibilityState>(() => {
    if (pendingColumnVisibility !== null) {
      return pendingColumnVisibility;
    }
    if (storedVisibility) {
      return { ...storedVisibility };
    }
    return {};
  }, [pendingColumnVisibility, storedVisibility]);

  const setColumnOrder = useCallback((order: ColumnOrderState) => {
    setPendingColumnOrder(order);
  }, []);

  const setColumnVisibility = useCallback((visibility: VisibilityState) => {
    setPendingColumnVisibility(visibility);
  }, []);

  const resetColumnState = useCallback(() => {
    setPendingColumnOrder(null);
    setPendingColumnVisibility(null);
  }, []);

  const isColumnStateDirty = useMemo(() => {
    const orderDirty =
      pendingColumnOrder !== null &&
      !arraysEqual(pendingColumnOrder, storedOrder ?? []);
    const visibilityDirty =
      pendingColumnVisibility !== null &&
      !visibilityRecordsEqual(pendingColumnVisibility, storedVisibility ?? {});
    return orderDirty || visibilityDirty;
  }, [
    pendingColumnOrder,
    pendingColumnVisibility,
    storedOrder,
    storedVisibility,
  ]);

  // What would be saved to a view if the user clicked Save-as-new-view or
  // Update view right now. Null means "no custom config; use table defaults"
  // — only returned when nothing has been customized at all (no pending state
  // and no stored state).
  const effectiveColumnsConfig = useMemo<ViewColumnsConfig | null>(() => {
    const hasPending =
      pendingColumnOrder !== null || pendingColumnVisibility !== null;
    const hasStored = storedOrder !== null || storedVisibility !== null;
    if (!hasPending && !hasStored) {
      return null;
    }
    const order = pendingColumnOrder ?? storedOrder ?? null;
    const visibility = pendingColumnVisibility ?? storedVisibility ?? {};
    return { order, visibility };
  }, [
    pendingColumnOrder,
    pendingColumnVisibility,
    storedOrder,
    storedVisibility,
  ]);

  return {
    columns,
    hideableColumnIds,
    columnOrder,
    setColumnOrder,
    columnSizing,
    setColumnSizing,
    columnVisibility,
    setColumnVisibility,
    isColumnStateDirty,
    effectiveColumnsConfig,
    resetColumnState,
  };
}
